import axios from 'axios';

// See AuthContext.js for why this wrapper exists: some browser contexts
// (Safari Private Browsing, in-app browsers, hardened privacy browsers)
// throw on any localStorage access. This interceptor runs on every single
// API call, so an unguarded call here would break the entire app's
// networking, not just auth — hence the extra care here specifically.
function safeGetItem(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // Ignore — nothing we can do if storage isn't accessible.
  }
}

function safeRemoveItem(key) {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // Ignore
  }
}

function safeClear() {
  try {
    localStorage.clear();
  } catch (e) {
    // Ignore
  }
}

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
});

API.interceptors.request.use((config) => {
  const token = safeGetItem('token');

  const isAuthRoute = config.url?.includes('/auth/') || config.url?.includes('/login');

  if (token && !isAuthRoute) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
};

const skipRefreshUrls = [
  '/api/auth/refresh',
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/logout',
  '/api/profile/location',
];

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      skipRefreshUrls.some((url) => originalRequest.url?.includes(url))
    ) {
      return Promise.reject(error);
    }

    const refreshToken = safeGetItem('refresh_token');
    if (!refreshToken) {
      safeClear();
      window.location.href = '/';
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return API(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const response = await axios.post(
        `${API.defaults.baseURL}/api/auth/refresh`,
        { refresh_token: refreshToken }
      );
      const newToken = response.data.token;
      safeSetItem('token', newToken);
      processQueue(null, newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return API(originalRequest);
  } catch (refreshError) {
  processQueue(refreshError, null);
  safeRemoveItem('user');
  safeRemoveItem('token');
  safeRemoveItem('refresh_token');
  safeRemoveItem('has_profile');
  window.location.href = '/';
  return Promise.reject(refreshError);
} finally {
      isRefreshing = false;
    }
  }
);

export default API;